/**
 * AppLayout - Core Carousel Layout Wrapper
 *
 * Provides the sidebar + main content layout for protected pages.
 * The editor page (fullscreen) does NOT use this layout.
 *
 * Manages:
 * - Sidebar navigation
 * - Editor theme (dark/light) state
 * - Dark class on document root
 * - Mobile drawer sidebar with hamburger menu
 */

import React, { useState, useEffect } from 'react';
import Sidebar from '@/components/Sidebar';
import CoreStudioMark from '@/components/ui/core-studio-mark';
import { useTranslation } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';
import { Menu, PanelLeft } from 'lucide-react';
import HelpChat from '@/components/help/HelpChat';

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const [editorTheme, setEditorTheme] = useState<'light' | 'dark'>('dark');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarHidden, setSidebarHidden] = useState(() => {
    try { return localStorage.getItem('sidebar_hidden') === 'true'; }
    catch { return false; }
  });
  const { t } = useTranslation();

  // Apply editor theme to document root
  useEffect(() => {
    if (editorTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [editorTheme]);

  const handleThemeToggle = () => {
    setEditorTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  // Scroll lock when drawer is open (prevents background scroll)
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  // ESC key to close drawer
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSidebarOpen(false);
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, []);

  // Close drawer when resizing to desktop (md: 768px)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = () => {
      if (mq.matches) setSidebarOpen(false);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Persist sidebar hidden state
  useEffect(() => {
    try { localStorage.setItem('sidebar_hidden', String(sidebarHidden)); }
    catch { /* noop */ }
  }, [sidebarHidden]);

  return (
    <div className="flex bg-background overflow-hidden" style={{ height: 'var(--app-height)' }}>
      {/* Mobile header - visible only < md */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 mobile-header bg-card border-b border-border z-40 flex items-center justify-between px-4">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 -ml-2 rounded-lg text-foreground hover:bg-accent transition-colors"
          aria-label={t('mobile.openMenu')}
          data-tour="mobile-menu-btn"
        >
          <Menu className="h-5 w-5" />
        </button>
        <CoreStudioMark size={24} />
        <div className="w-9" aria-hidden="true" />
      </div>

      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar - drawer on mobile, hideable on desktop */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50',
          'transition-transform duration-300',
          'md:relative md:transition-all md:duration-300',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          sidebarHidden ? 'md:-translate-x-full md:ml-[-260px] md:opacity-0' : 'md:translate-x-0 md:ml-0 md:opacity-100'
        )}
        role={sidebarOpen ? 'dialog' : undefined}
        aria-modal={sidebarOpen ? true : undefined}
        aria-label={sidebarOpen ? t('mobile.menu') : undefined}
      >
        <Sidebar
          editorTheme={editorTheme}
          onThemeToggle={handleThemeToggle}
          onClose={() => setSidebarOpen(false)}
          onHide={() => setSidebarHidden(true)}
          forceExpanded={sidebarOpen}
        />
      </div>

      {/* Main content - padding-top on mobile for header */}
      <main
        className={cn(
          'flex-1 overflow-auto pt-14 mobile-content-offset md:pt-0 transition-all duration-300',
          sidebarOpen && 'overflow-hidden'
        )}
      >
        {/* Floating button to show sidebar (desktop only, visible when hidden) */}
        {sidebarHidden && (
          <button
            onClick={() => setSidebarHidden(false)}
            className="hidden md:flex fixed top-4 left-4 z-30 items-center justify-center w-9 h-9 rounded-lg bg-card border border-border text-muted-foreground hover:bg-accent hover:text-foreground shadow-sm transition-colors"
            title={t('sidebar.show')}
          >
            <PanelLeft className="h-4 w-4" />
          </button>
        )}
        {children}
        <HelpChat />
      </main>
    </div>
  );
};

export default AppLayout;
